// directionalArrowTools.js — Bespoke draw interaction for 2-point DIRECTIONAL arrow rules.
// LINE9, LINE20, LINE25, LINE28 (tip/rear pairs) and LINE5 (bearing origin/direction pair).
//
// Unlike the symmetric fixed-count shapes handled by fixedPointCollector.js's single-click-
// and-spread (where swapping point order doesn't change the rendered shape at all — see that
// file's header comment), these 5 rules document an ASYMMETRIC point order in drawRuleMeta.js
// (P1 = arrowhead tip / bearing origin, P2 = rear of symbol / contact direction), so a
// single click can never correctly express the user's intended direction — placement needs
// two separately-aimed clicks.
//
// The remaining 2-point/fixed LINE rules originally considered for this treatment — LINE4,
// LINE14, LINE18, LINE19 — turned out NOT to need it on closer inspection of their documented
// point semantics: LINE4/LINE19 are plain "corner 1/corner 2" lines with no tip/rear concept
// at all (orientation is cosmetic, not meaningful), and LINE14/LINE18 are DOUBLE arrowheads
// where BOTH endpoints are tips — swapping click order changes nothing about how they render,
// so there is no "backwards arrow" bug to fix for them and they stay on single-click-spread
// (now lineSymmetricEndpointTools sequential preview).
//
// Two click behaviours, selected per rule:
//   - reverseOnFinish: true  (LINE9/20/25/28) — the natural placement gesture is "click my own
//     position (rear) first, then click toward the target (tip)", the OPPOSITE of the
//     library's documented P1=tip order — mirrors the AXIS1/2 fix in miscBespokeTools.js.
//   - reverseOnFinish: false (LINE5) — P1 is documented as the bearing ORIGIN (i.e. the
//     user's own position), which already matches "click my position first" directly.
//
// Live preview: tip-last + cursorFirst (applyReorderToPreview=false) so provisional =
// [cursor, ...raw] equals tip-to-front of [...raw, tip] — same as tipAtP1ArrowTools.

import { DrawRules } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import Feature from 'ol/Feature';
import { LineString } from 'ol/geom';
import { registerDrawInteraction } from '../customDrawInteractions';
import { createSequentialCollector, reverseAll } from './sequentialCollector';
import { PREVIEW_LINE_BLUE } from './drawPreviewStyles';

// P1 (anchor: the user's own position/rear) and P2 (direction: the aimed-at target/contact
// point) — regardless of whether finish() ends up reversing which collected index becomes
// which library point (see reverseOnFinish below), the SECOND click is always the one the user
// is actively "aiming" with, so it's tagged 'direction' by collected-click-order, not by final
// library point order.
function roleForIndex(i) { return i === 0 ? 'anchor' : 'direction'; }

function makeDirectionalArrowCollector(map, opts, { reverseOnFinish }) {
    return createSequentialCollector(map, {
        minCount: 2,
        maxCount: 2,
        onFinish: opts.onFinish,
        reorderOnFinish: reverseOnFinish ? reverseAll : (c) => c,
        sidc: opts.sidc || null,
        previewMinPoints: 1,
        cursorFirst: reverseOnFinish,
        applyReorderToPreview: false,
        roleForIndex,
        styleResolver: () => PREVIEW_LINE_BLUE,
        onSketchUpdate: ({ previewSource, collected3857, cursorCoord3857 }) => {
            const pts = cursorCoord3857 && collected3857.length === 1
                ? [...collected3857, cursorCoord3857]
                : collected3857;
            if (pts.length >= 2) previewSource.addFeature(new Feature(new LineString(pts)));
        },
    });
}

// LINE9/20/25/28: P1=arrowhead tip, P2=rear — natural gesture is rear-first, tip-last, so
// the clicked order must be reversed before handing off (same fix as AXIS1/2).
registerDrawInteraction(DrawRules.LINE9,  (map, opts) => makeDirectionalArrowCollector(map, opts, { reverseOnFinish: true }));
registerDrawInteraction(DrawRules.LINE20, (map, opts) => makeDirectionalArrowCollector(map, opts, { reverseOnFinish: true }));
registerDrawInteraction(DrawRules.LINE25, (map, opts) => makeDirectionalArrowCollector(map, opts, { reverseOnFinish: true }));
registerDrawInteraction(DrawRules.LINE28, (map, opts) => makeDirectionalArrowCollector(map, opts, { reverseOnFinish: true }));

// LINE5: P1=bearing origin, P2=contact location/direction — already matches "click my
// position first, then the target" directly, so no reversal is needed.
registerDrawInteraction(DrawRules.LINE5, (map, opts) => makeDirectionalArrowCollector(map, opts, { reverseOnFinish: false }));
